import AdyenCheckout from '@adyen/adyen-web';
// eslint-disable-next-line import/no-unresolved
import { PaymentMethodsResponseObject } from '@adyen/adyen-web/dist/types/core/ProcessResponse/PaymentMethodsResponse/types';
// eslint-disable-next-line import/no-unresolved
import { PaymentMethods } from '@adyen/adyen-web/dist/types/types';
import { useEffect } from 'react';
import { TFunction } from 'react-i18next';

import { Locale } from '../../../../types';
import { safeStringify } from '../../../../utils/utils';
import { normalizeLocaleForAdyen } from '../../Adyen.static';
import { AdyenConfig } from '../../Adyen.types';
import { APPLE_PAY_CONTAINER_ID, extractApplePayToken } from '../AdyenApplePayButton.static';

export interface AdyenApplePayConfig extends AdyenConfig {
  locale: Locale;
  t: TFunction<'translation', undefined>;
  countryCode: string;
  currency: string;
  amount: number;
  paymentMethodsResponse: unknown;
  onSubmit: (applePayToken: string) => void;
  onFatalError: (error: unknown) => void;
}

/**
 * Effect hook over the initialization of the 'applepay' of AdyenCheckout.
 */
const useAdyenApplePay = (config: AdyenApplePayConfig) => {
  const {
    adyenEnvironment,
    clientKey,
    locale,
    countryCode,
    currency,
    amount,
    paymentMethodsResponse,
    onSubmit,
    onFatalError,
  } = config;

  useEffect(() => {
    let applePayComponent: InstanceType<PaymentMethods['applepay']> | null = null;

    async function initialize() {
      try {
        const adyenLocale = normalizeLocaleForAdyen(locale);
        const adyen = await AdyenCheckout({
          environment: adyenEnvironment,
          clientKey,
          locale: adyenLocale,
          paymentMethodsResponse: paymentMethodsResponse as PaymentMethodsResponseObject,
        });

        applePayComponent = adyen.create('applepay', {
          onSubmit: (state: unknown) => {
            const maybeApplePayToken = extractApplePayToken(state);
            if (maybeApplePayToken === null) {
              onFatalError(
                new Error(`Failed to extract the applePayToken from ${safeStringify(state)}`),
              );
            } else {
              onSubmit(maybeApplePayToken);
            }
          },
          countryCode,
          amount: {
            value: amount,
            currency,
          },
          buttonType: 'buy',
          buttonColor: 'black',
          onError: onFatalError,
        }) as unknown as InstanceType<PaymentMethods['applepay']>;

        await applePayComponent.isAvailable();
        applePayComponent.mount(`#${APPLE_PAY_CONTAINER_ID}`);
      } catch (error) {
        onFatalError(error);
      }
    }

    initialize();

    return () => {
      if (applePayComponent) {
        applePayComponent.unmount();
      }
    };
  }, [
    adyenEnvironment,
    locale,
    clientKey,
    countryCode,
    amount,
    currency,
    paymentMethodsResponse,
    onSubmit,
    onFatalError,
  ]);
};

export default useAdyenApplePay;
